import { HttpClient } from "@angular/common/http";
import { inject, Injectable } from "@angular/core";
import { ApiRouting } from "@globals/classes/ApiRouting";
import { BiLanguageId } from "@globals/enums/BiLanguageAndCountryId";
import { AnonymousTokenModel } from "@globals/openapi-models/model/anonymousTokenModel";
import { PageNames } from "@globals/openapi-models/model/pageNames";
import { PinCodeRequestDto } from "@globals/openapi-models/model/pinCodeRequestDto";
import { SubscribeAddressCommand } from "@globals/openapi-models/model/subscribeAddressCommand";
import { SubscribeSupplyNumberCommand } from "@globals/openapi-models/model/subscribeSupplyNumberCommand";
import { SubscriptionDto } from "@globals/openapi-models/model/subscriptionDto";

import { SubscriptionModuleModel } from "@globals/openapi-models/model/subscriptionModuleModel";
import { UnsubscribeAddressCommand } from "@globals/openapi-models/model/unsubscribeAddressCommand";
import { UnsubscribeSupplyNumberCommand } from "@globals/openapi-models/model/unsubscribeSupplyNumberCommand";
import { VerifyPinCodeRequestDto } from "@globals/openapi-models/model/verifyPinCodeRequestDto";

@Injectable()
export class SubscribeModuleBaseService {
  protected http: HttpClient;

  constructor() {
    this.http = inject(HttpClient);
  }

  /**
   *  Returns all the addresses that the provided phone number and/or email is subscribed to
   */
  public getSubscriptions(
    accessToken: string,
    customerId: number,
    phoneNumber?: string,
    email?: string,
    phoneVoice?: string,
    onlyBusiness?: boolean,
    onlyPrivate?: boolean,
    noOperatorLookup?: boolean
  ) {
    const params = {
      customerId: customerId,
      accessToken: accessToken
    };
    if (phoneNumber) params["phoneNumber"] = encodeURIComponent(phoneNumber);
    if (email) params["email"] = email;
    if (onlyBusiness) params["onlyBusiness"] = "true";
    if (onlyPrivate) params["onlyPrivate"] = "true";
    if (phoneVoice) params["phoneVoice"] = encodeURIComponent(phoneVoice);
    if (noOperatorLookup) params["noOperatorLookup"] = "true";

    return this.http.get<SubscriptionDto[]>(ApiRouting.subscribeModuleEndpoints.get.getSubscriptions, { params: params });
  }

  /**
   * Returns configuration model for the Subscription module.
   * @param customerPublicId The customer's publicId, e.g. a GUID and NOT the customer id referring to db id.
   */
  public getSubscriptionModuleModel(customerPublicId: string, languageId?: BiLanguageId) {
    const params = { customerPublicId: customerPublicId };
    if (languageId) params["languageId"] = languageId;

    return this.http.get<SubscriptionModuleModel>(ApiRouting.subscribeModuleEndpoints.get.getSubscriptionModuleModel, {
      params: params
    });
  }

  public subscribeAddress(subscribeAddressCommand: SubscribeAddressCommand) {
    return this.http.post(ApiRouting.subscribeModuleEndpoints.post.subscribeAddress, subscribeAddressCommand);
  }

  public unsubscribeAddress(unsubscribeAddressCommand: UnsubscribeAddressCommand) {
    return this.http.post(ApiRouting.subscribeModuleEndpoints.post.unsubscribeAddress, unsubscribeAddressCommand);
  }

  public unsubscribeSupplyNumber(unsubscribeSupplyNumberCommand: UnsubscribeSupplyNumberCommand) {
    return this.http.post(ApiRouting.subscribeModuleEndpoints.post.unsubscribeSupplyNumber, unsubscribeSupplyNumberCommand);
  }

  public unsubscribeEnrollment(enrollmentId: number) {
    return this.http.post(ApiRouting.subscribeModuleEndpoints.post.unsubscribeEnrollmentById, enrollmentId);
  }

  public subscribeSupplyNumber(subscribeCommand: SubscribeSupplyNumberCommand) {
    return this.http.post(ApiRouting.subscribeModuleEndpoints.post.subscribeSupplyNumber, subscribeCommand);
  }

  /**
   * For generating a security PIN code and send it to provided email and/or phone number.
   * Returns a AnonymousToken if user is already authenticated in smsservice app
   */
  public requestPinCodeAttempt(requestDto: PinCodeRequestDto) {
    return this.http.post<AnonymousTokenModel>(ApiRouting.subscribeModuleEndpoints.requestPinCodeAttempt, requestDto);
  }

  /**
   * Verifies a PIN code generated using the RequestPinCode() API method.
   */
  public verifyPinCode(customerId: string, pageNameId: PageNames, code: number, phoneNumber?: string, landline?: string, email?: string) {
    const params: VerifyPinCodeRequestDto = {
      code: code,
      phoneNumber: phoneNumber,
      landline: landline,
      email: email,
      pageNameId: pageNameId,
      customerId: customerId
    };

    return this.http.post<AnonymousTokenModel>(ApiRouting.subscribeModuleEndpoints.verifyPinCode, params);
  }
}
